/**
 * The plan screen's billing half, as pure logic: what Stripe sent the browser
 * back with, how an amount reads, and which cards the Monthly / Yearly switch
 * shows. `components/BillingSection.tsx` draws it; `api/billing.ts` fetches it.
 *
 * **Nothing here knows a price.** Amounts arrive from `GET /billing/plans`,
 * which reads them off the Stripe price — so a price changed in the dashboard
 * changes this screen with no deploy, and the screen cannot quote a number the
 * checkout page then contradicts.
 */

export type Interval = "month" | "quarter" | "year";

export interface Offer {
  plan: string;
  interval: Interval;
  /** Minor units — cents. */
  amount: number;
  currency: string;
  name: string;
  description: string;
  cta: string;
  /** What the first period costs, when it differs: SweeterTrade's first year. */
  firstAmount?: number;
}

/**
 * Another plan the held one may move to now (`payments.md` § The first
 * release): `amount` is charged today, the renewal date stays, and the next
 * renewal is `nextAmount` — then `laterAmount` from the one after.
 */
export interface Upgrade {
  plan: string;
  interval: Interval;
  name: string;
  description: string;
  currency: string;
  amount: number;
  renewsAt: number | null;
  nextAmount: number;
  laterAmount: number;
}

export interface Held extends Offer {
  status: string;
  /** Unix seconds, or null. */
  renewsAt: number | null;
  cancelAtPeriodEnd: boolean;
  /** A pause scheduled or under way. Absent from an older server. */
  pause?: Pause | null;
  /** What pausing would do, when no pause is set. Absent from an older server. */
  pauseOffer?: PauseOffer | null;
  /** What it may upgrade to. Absent from an older server. */
  upgrades?: Upgrade[];
}

/** `today` or the next `renewal` — the subscriber's choice. */
export type PauseStart = "today" | "renewal";

/**
 * One pause: Free from `startsAt` to `endsAt`, next charge `renewsAfter`.
 * Unix seconds. See `services/qv-data/app/billing/pause.py`.
 */
export interface PauseWindow {
  start: PauseStart;
  days: number;
  startsAt: number;
  endsAt: number;
  renewsAfter: number;
}

export interface Pause extends PauseWindow {
  state: "scheduled" | "active";
}

export interface PauseOffer {
  /** Why the plan cannot be paused now, or null. */
  refused: string | null;
  daysLeft: number;
  allowance?: number;
  year?: number;
  today?: PauseWindow | null;
  renewal?: PauseWindow | null;
}

export interface BillingPlans {
  enabled: boolean;
  offers: Offer[];
  current: Held | null;
  /** A Stripe customer exists — invoices to show, subscribed or not. Absent from an older server. */
  hasBilling?: boolean;
}

/** `?billing=` on the URL Stripe sends the browser back to. */
export type BillingReturn = "done" | "cancelled" | "portal";

const RETURNS: ReadonlySet<string> = new Set(["done", "cancelled", "portal"]);

export function readBillingReturn(search: string): BillingReturn | null {
  const value = new URLSearchParams(search).get("billing");
  return value !== null && RETURNS.has(value) ? (value as BillingReturn) : null;
}

/** The URL without `?billing=`, so a refresh does not replay the return. */
export function withoutBillingReturn(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("billing");
  return url.toString();
}

/**
 * **The plan a landing-page button chose** — `?plan=qf-tier1&interval=year`
 * (`payments.md` § QuFolio went live). Kept in `sessionStorage` from the first
 * load, because the sign-in that follows may navigate away and back without
 * it; taken once, so a later reload does not reopen Your plan.
 */
export interface PlanIntent {
  plan: string;
  interval: Interval;
}

const INTENT_KEY = "qf.planIntent";
const PLAN_KEY = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function readPlanIntent(search: string): PlanIntent | null {
  const params = new URLSearchParams(search);
  const plan = params.get("plan");
  if (plan === null || !PLAN_KEY.test(plan)) return null;
  return { plan, interval: asInterval(params.get("interval")) };
}

function asInterval(value: unknown): Interval {
  return value === "year" || value === "quarter" ? value : "month";
}

/** The URL without `?plan=` / `?interval=`. */
export function withoutPlanIntent(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("plan");
  url.searchParams.delete("interval");
  return url.toString();
}

/** Before sign-in: move a chosen plan off the URL into this tab's session. */
export function capturePlanIntent(): void {
  const intent = readPlanIntent(window.location.search);
  if (intent === null) return;
  window.history.replaceState({}, "", withoutPlanIntent(window.location.href));
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
  } catch {
    // Storage refused: the choice is lost and Your plan opens on nothing. Fine.
  }
}

/** Signed in: the chosen plan, once. */
export function takePlanIntent(): PlanIntent | null {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    sessionStorage.removeItem(INTENT_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<PlanIntent>;
    return typeof parsed.plan === "string" && PLAN_KEY.test(parsed.plan)
      ? { plan: parsed.plan, interval: asInterval(parsed.interval) }
      : null;
  } catch {
    return null;
  }
}

/** `$14.99`, `$335` — no `.00` on a whole amount, as the landing page writes it. */
export function formatAmount(amount: number, currency: string): string {
  const whole = amount % 100 === 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(amount / 100);
  } catch {
    // An unknown ISO code: say the number rather than throw inside a render.
    return `${(amount / 100).toFixed(whole ? 0 : 2)} ${currency.toUpperCase()}`;
  }
}

export function perInterval(interval: Interval): string {
  return interval === "year" ? "/ year" : interval === "quarter" ? "/ quarter" : "/ month";
}

const SWITCH_ORDER: Interval[] = ["month", "quarter", "year"];
const SWITCH_LABEL: Record<Interval, string> = { month: "Monthly", quarter: "Quarterly", year: "Yearly" };

/** The switch's sides: the intervals sold at all, in order. One side is no switch. */
export function switchSides(offers: Offer[]): { interval: Interval; label: string }[] {
  const sold = new Set(offers.map((offer) => offer.interval));
  return SWITCH_ORDER.filter((i) => sold.has(i)).map((interval) => ({ interval, label: SWITCH_LABEL[interval] }));
}

/**
 * Which side the switch opens on: the one asked for, else Monthly where it is
 * sold (QuFolio), else Yearly (SweeterTrade's mock opens on Yearly).
 */
export function openingSide(offers: Offer[], asked: Interval | null): Interval {
  const sides = switchSides(offers).map((side) => side.interval);
  if (asked && sides.includes(asked)) return asked;
  if (sides.includes("month")) return "month";
  return sides.includes("year") ? "year" : (sides[0] ?? "month");
}

/**
 * The cards for one side of the switch, cheapest first.
 *
 * A plan sold only monthly still shows on the yearly side — at its monthly
 * price, labelled as such — because hiding it would read as "this plan does not
 * exist" to someone who merely flipped a switch.
 */
export function cardsFor(offers: Offer[], interval: Interval): Offer[] {
  const byPlan = new Map<string, Offer>();
  for (const offer of offers) {
    const held = byPlan.get(offer.plan);
    if (!held || (held.interval !== interval && offer.interval === interval)) {
      byPlan.set(offer.plan, offer);
    }
  }
  return [...byPlan.values()].sort((a, b) => monthly(a) - monthly(b));
}

function monthly(offer: Offer): number {
  if (offer.interval === "year") return offer.amount / 12;
  return offer.interval === "quarter" ? offer.amount / 3 : offer.amount;
}

/** Whether there is a yearly price to switch to at all. */
export function hasYearly(offers: Offer[]): boolean {
  return offers.some((offer) => offer.interval === "year");
}

/**
 * `11 Nov 2026` — **always with the year** (owner, 2026-10-12): a renewal or
 * an end date is a date somebody acts on, and a yearly plan's is next year.
 */
export function formatRenewal(unixSeconds: number): string {
  // **In UTC, as Stripe states it.** A period ending 03:47 UTC on 13 Nov is the
  // evening of the 12th in Pacific time; read locally, the plan said 12 Nov
  // while Stripe's portal and receipts said Nov 13 (2026-10-12).
  return new Date(unixSeconds * 1000).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The line under a held plan: when it renews, when a cancelled one ends, or
 * that a payment failed. Cancel is **at period end** — the plan stays until the
 * date paid for, and Stripe's portal offers to renew it until then.
 */
export function heldStatus(held: Held): { text: string; warn: boolean } | null {
  const pause = held.pause;
  if (pause?.state === "active") {
    return {
      text: `Paused until ${formatRenewal(pause.endsAt)} — next charge ${formatRenewal(pause.renewsAfter)}.`,
      warn: true,
    };
  }
  if (pause?.state === "scheduled") {
    return {
      text: `Pauses ${formatRenewal(pause.startsAt)} — ${held.name} returns ${formatRenewal(pause.endsAt)}, next charge ${formatRenewal(pause.renewsAfter)}.`,
      warn: false,
    };
  }
  if (held.status === "past_due") {
    return { text: "Payment failed — retrying. Update your card in Manage billing.", warn: true };
  }
  if (held.renewsAt === null) return null;
  const date = formatRenewal(held.renewsAt);
  if (held.cancelAtPeriodEnd) {
    return {
      text: `Cancelled — ${held.name} until ${date}, then Free. To keep it, choose Renew plan in Manage billing.`,
      warn: true,
    };
  }
  return { text: `Auto-renews ${date}`, warn: false };
}

/** `Monthly`, `Yearly`, `1 year`, `2 years`, `Forever` — how a term reads on screen. */
export function termLabel(interval: string): string {
  if (interval === "month") return "Monthly";
  if (interval === "year") return "Yearly";
  if (interval === "once-forever") return "Forever";
  const years = /^once-(\d+)y$/.exec(interval);
  if (years) return years[1] === "1" ? "1 year" : `${years[1]} years`;
  return interval;
}

/** What an admin may put on sale, in the order the picker lists them. */
export const SALE_TERMS = [
  "month",
  "year",
  "once-1y",
  "once-2y",
  "once-3y",
  "once-5y",
  "once-forever",
] as const;

/**
 * The plan to name beside the account — the subscription, not `me.plan`.
 *
 * `me.plan` is the account's stored tier, which billing never writes: a paid
 * plan arrives as a club (`qf-eod`), so `me.plan` reads `free` on a paying
 * account. `null` when billing is off here, so the caller keeps its old line.
 */
export function heldPlanName(billing: BillingPlans | null, withInterval = false): string | null {
  if (!billing?.enabled) return null;
  const held = billing.current;
  if (!held) return "Free";
  if (held.pause?.state === "active") {
    return `${held.name} · paused until ${formatRenewal(held.pause.endsAt)}`;
  }
  // A cancelled plan says when it goes, wherever the plan is named.
  if (held.cancelAtPeriodEnd && held.renewsAt !== null) {
    return `${held.name} · ends ${formatRenewal(held.renewsAt)}`;
  }
  return withInterval ? `${held.name} · ${SWITCH_LABEL[held.interval].toLowerCase()}` : held.name;
}

/**
 * Clubs a person chose, as names — not the ones their plan brings.
 *
 * `qf-*` clubs come with the tier (`qf-eod` and `qf-us` with EOD) and are the
 * plan line said another way. A group is `grp-<slug>`; `/auth/me` sends keys
 * only, so the slug is the name until it sends names.
 */
export function joinedGroups(orgs: string[]): string[] {
  return orgs
    .filter((org) => !org.startsWith("qf-"))
    .map((org) => (org.startsWith("grp-") ? org.slice(4).toUpperCase() : org));
}

/**
 * The line under the pause choices when no pause is left: the year's 30 days
 * are spent. `null` while days remain, or when pausing is refused for another
 * reason (a payment owed, a cancelled plan) — those say nothing here.
 */
export function pauseUsedUp(offer: PauseOffer | null | undefined): string | null {
  if (!offer || offer.refused !== null || offer.daysLeft > 0 || offer.year === undefined) return null;
  return `No pause days left for ${offer.year} — ${offer.allowance ?? 30} more from 1 Jan ${offer.year + 1}.`;
}

/**
 * The menu's buy button under the plan line (owner's mock, 2026-10-31):
 * **Choose a plan…** on Free when anything is on sale, **Upgrade to Pro…** when
 * the held plan has an upgrade, and nothing otherwise — the button appears only
 * when there is something to buy. Both open Your plan…, where the cards are.
 */
export function buyLabel(billing: BillingPlans | null): string | null {
  if (!billing?.enabled) return null;
  const held = billing.current;
  if (!held) return billing.offers.length > 0 ? "Choose a plan…" : null;
  const up = held.upgrades?.[0];
  return up ? `Upgrade to ${up.name}…` : null;
}
